/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import React from 'react';
import _ from 'lodash';
import { decorate, computed, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Container, Icon } from 'semantic-ui-react';

import { gotoFn } from '@amzn/base-ui/dist/helpers/routing';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { isStoreLoading, isStoreEmpty, isStoreNotEmpty, isStoreError } from '@amzn/base-ui/dist/models/BaseStore';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';

import KeyPairCard from './KeyPairCard';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

class KeyPairsList extends React.Component {
  componentDidMount() {
    window.scrollTo(0, 0);
    const store = this.keyPairsStore;
    swallowError(store.load());
  }

  get keyPairsStore() {
    return this.props.keyPairsStore;
  }

  handleCreateKeyPair = event => {
    event.preventDefault();
    event.stopPropagation();
    const goto = gotoFn(this);
    goto('/key-pair-management/create');
  };

  render() {
    const store = this.keyPairsStore;
    let content = null;

    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} className="p0" />;
    } else if (isStoreLoading(store)) {
      content = <ProgressPlaceHolder segmentCount={1} />;
    } else if (isStoreEmpty(store)) {
      content = this.renderEmpty();
    } else if (isStoreNotEmpty(store)) {
      content = this.renderMain();
    }

    return (
      <Container className="mt3 animated fadeIn">
        {this.renderTitle()}
        {content}
      </Container>
    );
  }

  renderMain() {
    const list = this.keyPairsStore.list;
    return (
      <div className="ssh-key-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {_.map(list, item => (
          <KeyPairCard keyPair={item} key={item.id} />
        ))}
      </div>
    );
  }

  renderEmpty() {
    return (
      <div className="workspace-modern-table-empty">
        <Icon name="terminal" style={{ fontSize: '2.5em', color: '#d1d5db', marginBottom: '12px' }} />
        <div className="workspace-modern-table-empty-title">No SSH Keys</div>
        <div className="workspace-modern-table-empty-text">To create an SSH key, click Create Key.</div>
      </div>
    );
  }

  renderTitle() {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
          <button className="workspace-action-btn workspace-action-btn-primary" onClick={this.handleCreateKeyPair}>
            <Icon name="plus" style={iconStyle} /> Create Key
          </button>
        </div>
      );
    }
}

decorate(KeyPairsList, {
  keyPairsStore: computed,
  handleCreateKeyPair: action,
});

export default inject('keyPairsStore')(withRouter(observer(KeyPairsList)));
